import { VirtualElement } from '@popperjs/core';
import { MinimalEditor } from '@sketchnote/minimal-editor';
import { Avatar, Button, IconTextBtn } from '@sketchnote/shared-components';
import {
  ELEMENT_DEFAULT,
  PlateProvider,
  TElement,
  usePlateEditorRef,
  usePlateEditorState,
} from '@udecode/plate';
import { PortalBody } from '@udecode/plate-styled-components';
import cn from 'classnames';
import { formatRelative, parseISO } from 'date-fns';
import isHotKey from 'is-hotkey';
import * as React from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { usePopper } from 'react-popper';
import { Editor } from 'slate';
import { ReactEditor } from 'slate-react';
import { v4 as uuid } from 'uuid';

import { useSlatePluginsStore } from '../store';
import { resetEditorValue } from '../utils';
import { MARK_COMMENT_ID } from '.';
import { useComments } from './useComments';
import { getCommentNodeEntry } from './utils';

const { useEffect, useRef, useState, Fragment, useCallback, useMemo } = React;

const virtualReference: VirtualElement = {
  getBoundingClientRect() {
    return {
      bottom: 20,
      height: 10,
      left: 10,
      right: 100,
      top: 10,
      width: 90,
      x: 0,
      y: 0,
      toJSON: () => null,
    };
  },
};

export const Comments = ({
  editorId,
  isPublicFile = false,
}: {
  editorId: string;
  isPublicFile?: boolean;
}) => {
  const activeComment = useComments((s) => s.activeComment);
  const [elem] = useState(document?.getElementById('document-wrapper'));
  const membersRef = useRef(document?.getElementById('mention-minimal-editor-list-wrapper'));
  const setActiveComment = useComments((s) => s.setActiveComment);
  const addComment = useComments((s) => s.addComment);
  const deleteComment = useComments((s) => s.deleteComment);
  const comments = useComments((s) => s.comments);
  const currentUser = useSlatePluginsStore((s) => s.currentUser);
  const ref = useRef<HTMLDivElement>(null);
  const currentComment = activeComment?.id ? comments[activeComment?.id] : null;

  const editor = usePlateEditorState(editorId);
  const { styles, attributes, update } = usePopper(virtualReference, ref.current, {
    placement: 'bottom',
    modifiers: elem
      ? [
          {
            name: 'preventOverflow',
            options: {
              boundary: elem ? elem : undefined,
            },
          },
          {
            name: 'flip',
            options: {
              allowedAutoPlacements: ['top', 'bottom'],
              padding: elem?.clientHeight
                ? {
                    // TODO: Hackiest way to position because of the boundary element not working properly
                    top: -((elem?.clientHeight || 0) * 0.45),
                  }
                : undefined,
              altBoundary: true,
            },
          },
          { name: 'eventListeners', options: { scroll: !!activeComment } },
        ]
      : undefined,
  });

  useOnclickOutside(
    () => {
      if (activeComment) {
        setActiveComment(null);
      }
    },
    { refs: [ref, membersRef] }
  );

  const setPosition = useCallback(() => {
    if (activeComment?.selection) {
      const domRange = ReactEditor.toDOMRange(editor as ReactEditor, activeComment.selection);
      const rect = domRange.getBoundingClientRect();
      virtualReference.getBoundingClientRect = () => rect;
      update?.();
    }
  }, [activeComment?.selection, update]);

  useEffect(() => {
    setPosition();
  }, [activeComment?.selection, setPosition]);

  useEffect(() => {
    const elem = document.getElementById('file-wrapper') || document;
    elem?.addEventListener('scroll', setPosition);
    return () => {
      elem?.removeEventListener('scroll', setPosition);
    };
  }, [setPosition]);

  const handleResolveComment = async () => {
    if (activeComment?.id && editor && activeComment.selection) {
      editor.selection = activeComment.selection;
      const isDeleted = await deleteComment({ id: activeComment?.id });
      if (isDeleted) {
        editor.removeMark(MARK_COMMENT_ID);
      }
      editor.selection = null;
      setActiveComment(null);
    }
  };

  return (
    <PortalBody>
      <div
        {...attributes.popper}
        style={styles.popper}
        ref={ref}
        className={cn({ hidden: !activeComment })}
      >
        <div className="w-364 bg-white rounded-lg p-16 shadow-sm max-h-400 overflow-auto">
          {currentComment ? (
            <Fragment>
              <div className="mb-16">
                <div className="flex items-center mb-8">
                  <Avatar name={currentComment.owner.firstname} src={currentComment.owner.avatar} />
                  <div className="items-center flex-1 ml-12">
                    <p className="text-sm">{currentComment.owner.firstname}</p>
                    <p className="text-xss text-gray-600">
                      {formatRelative(parseISO(currentComment.updatedAt), new Date())}
                    </p>
                  </div>
                  <IconTextBtn color="gray" className="p-10" onClick={handleResolveComment}>
                    Resolve
                  </IconTextBtn>
                </div>
                <div className="text-sm">
                  <MinimalEditor
                    disabled
                    editorValue={currentComment.content}
                    id={currentComment.id}
                  />
                </div>
              </div>
              <div className="ml-8">
                {currentComment.replies?.map((reply) => (
                  <div className="mb-16" key={reply.id}>
                    <div className="flex items-center mb-8" key={reply.id}>
                      <Avatar name={reply.owner.firstname} src={reply.owner.avatar} />
                      <div className="items-center flex-1 ml-12">
                        <p className="text-sm">{reply.owner.firstname}</p>
                        <p className="text-xss text-gray-600">
                          {formatRelative(parseISO(reply.updatedAt), new Date())}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm">
                      <MinimalEditor disabled editorValue={reply.content} id={reply.id} />
                    </div>
                  </div>
                ))}
              </div>
            </Fragment>
          ) : null}

          {!isPublicFile && (
            <NewComment
              onSubmit={async (e) => {
                if (activeComment?.selection && editor) {
                  const id = await addComment({
                    value: e,
                    id: activeComment?.id,
                    user: {
                      ...currentUser,
                    },
                  });
                  if (id) {
                    editor.selection = { ...activeComment.selection };
                    editor.addMark(MARK_COMMENT_ID, id);
                    editor.selection = null;
                    const nodeEntry = getCommentNodeEntry(id, editor);

                    if (nodeEntry) {
                      const pathT = nodeEntry[1];
                      const range = {
                        anchor: {
                          path: pathT,
                          offset: 0,
                        },
                        focus: Editor.end(editor, pathT),
                      };
                      setActiveComment({ ...activeComment, selection: range, id });
                    }
                  } else {
                    setActiveComment({ ...activeComment });
                  }
                }
              }}
            />
          )}
        </div>
      </div>
    </PortalBody>
  );
};

const NewCommentBase = ({
  editorId,
  onSubmit,
}: {
  editorId: string;
  onSubmit: (value: TElement[]) => void;
}) => {
  const [input, setInput] = useState(initialValue);
  const currentUser = useSlatePluginsStore((s) => s.currentUser);
  const members = useSlatePluginsStore((s) => s.members);
  const editor = usePlateEditorRef(editorId)!;

  const handleOnSubmit = () => {
    onSubmit(editor.children);
    editor && resetEditorValue(initialValue, editor);
    setInput(initialValue);
  };
  return (
    <div className="flex items-center w-full">
      <Avatar name={currentUser.firstname} src={currentUser.avatar} />
      <div className="flex-1 ml-12 break-words">
        <MinimalEditor
          editorValue={input}
          id={editorId}
          onEditorChange={setInput}
          members={members}
          onKeyDownHandler={(e) => {
            if (isHotKey('mod+enter', e)) {
              handleOnSubmit();
            }
          }}
        />
      </div>
      <Button onClick={handleOnSubmit}>Submit</Button>
    </div>
  );
};

const NewCommentEditorProvider = ({ id, children }: { id: string; children: React.ReactNode }) => {
  return <PlateProvider id={id}>{children}</PlateProvider>;
};

const NewComment = ({ onSubmit }: { onSubmit: (value: TElement[]) => void }) => {
  const [editorId] = useState(uuid());

  return (
    <NewCommentEditorProvider id={editorId}>
      <NewCommentBase editorId={editorId} onSubmit={onSubmit} />
    </NewCommentEditorProvider>
  );
};

const initialValue: TElement[] = [{ children: [{ text: '' }], type: ELEMENT_DEFAULT }];
